/**
 * DashboardPage — hybrid command-centre redesign.
 *
 * Replaces the marketing-hero landing with a work-focused layout:
 *   • WelcomeHeader      — compact greeting + primary CTA (no big hero)
 *   • StatsStrip         — three derived stats (total / calculated / in-progress)
 *                          from useHistoryStorage; skeleton while loading;
 *                          empty-state invite when there are no engagements
 *   • RecentEngagements  — last 3 by updatedAt; hidden when empty; skeleton
 *   • TrustStrip         — single border-top row of three icon+text items
 *
 * Feature cards (calc/mod/fx) were intentionally removed. All copy is
 * i18n-driven via `t("dashboard.*")`. Colours ride theme tokens only
 * (background / foreground / muted / primary) for dark/light parity.
 */

import { format, parseISO } from "date-fns";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  FileText,
  Lock,
  Plus,
  RefreshCw,
  Zap,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StatusBadge } from "@/features/engagement/components/StatusBadge";
import { deriveEngagementStatus } from "@/features/engagement/utils/deriveEngagementStatus";
import { useHistoryStorage } from "@/services";
import type { EngagementHistoryEntry } from "@/types";

// ─── Shared async state for engagements (one fetch, used by stats + recent) ──

type EngagementsState =
  | { kind: "loading" }
  | { kind: "ready"; entries: EngagementHistoryEntry[] };

function useEngagements(): EngagementsState {
  const storage = useHistoryStorage();
  const [state, setState] = React.useState<EngagementsState>({
    kind: "loading",
  });

  React.useEffect(() => {
    let cancelled = false;
    void storage
      .listEngagements()
      .then((all) => {
        if (cancelled) return;
        const live = all.filter((e) => e.isDeleted !== true);
        setState({ kind: "ready", entries: live });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[dashboard] listEngagements failed", err);
        if (!cancelled) setState({ kind: "ready", entries: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [storage]);

  return state;
}

export function DashboardPage() {
  const state = useEngagements();
  return (
    <div className="flex flex-col gap-2">
      <WelcomeHeader />
      <StatsStrip state={state} />
      <RecentEngagements state={state} />
      <TrustStrip />
    </div>
  );
}

// ─── Welcome header ─────────────────────────────────────────────────────────

function WelcomeHeader() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section className="flex flex-col gap-4 py-8 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="text-sm font-medium text-muted-foreground mb-1">
          {t("dashboard.welcome.kicker")}
        </p>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">
          {t("dashboard.welcome.title")}
        </h1>
        <p className="mt-2 max-w-prose text-sm md:text-base text-muted-foreground leading-relaxed">
          {t("dashboard.welcome.subtitle")}
        </p>
      </div>
      <div className="shrink-0">
        <Button
          size="lg"
          onClick={() => navigate("/engagements/new")}
          className="gap-2"
          data-testid="dashboard-cta-new-engagement"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t("dashboard.welcome.cta")}
        </Button>
      </div>
    </section>
  );
}

// ─── Stats strip ──────────────────────────────────────────────────────────

interface StatTriple {
  total: number;
  calculated: number;
  inProgress: number;
}

function computeStats(entries: EngagementHistoryEntry[]): StatTriple {
  let calculated = 0;
  let inProgress = 0;
  for (const e of entries) {
    const status = deriveEngagementStatus(e);
    if (status === "calculated") calculated += 1;
    else inProgress += 1; // draft | stale
  }
  return { total: entries.length, calculated, inProgress };
}

function StatsStrip({ state }: { state: EngagementsState }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (state.kind === "loading") {
    return (
      <section
        className="grid grid-cols-1 gap-3 sm:grid-cols-3"
        data-testid="dashboard-stats-loading"
      >
        {[0, 1, 2].map((i) => (
          <Card key={i} className="p-5 animate-pulse">
            <div className="h-3 w-24 rounded bg-muted mb-3" />
            <div className="h-8 w-12 rounded bg-muted" />
          </Card>
        ))}
      </section>
    );
  }

  // Empty state — no engagements yet: a single inviting card spanning the row.
  if (state.entries.length === 0) {
    return (
      <section data-testid="dashboard-stats-empty">
        <Card className="flex flex-col items-start gap-3 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <FileText className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                {t("dashboard.stats.emptyTitle")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("dashboard.stats.emptyHint")}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate("/engagements/new")}
            className="gap-2 shrink-0"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t("dashboard.welcome.cta")}
          </Button>
        </Card>
      </section>
    );
  }

  const stats = computeStats(state.entries);
  const items = [
    {
      icon: ClipboardList,
      label: t("dashboard.stats.total"),
      value: stats.total,
      testId: "dashboard-stat-total",
    },
    {
      icon: CheckCircle2,
      label: t("dashboard.stats.calculated"),
      value: stats.calculated,
      testId: "dashboard-stat-calculated",
    },
    {
      icon: RefreshCw,
      label: t("dashboard.stats.inProgress"),
      value: stats.inProgress,
      testId: "dashboard-stat-inprogress",
    },
  ];

  return (
    <section
      className="grid grid-cols-1 gap-3 sm:grid-cols-3"
      data-testid="dashboard-stats"
    >
      {items.map(({ icon: Icon, label, value, testId }) => (
        <Card key={testId} data-testid={testId} className="p-5">
          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <Icon className="h-4 w-4" aria-hidden="true" />
            <span className="text-xs font-medium uppercase tracking-wide">
              {label}
            </span>
          </div>
          <p className="text-3xl font-semibold tabular-nums text-foreground">
            {value}
          </p>
        </Card>
      ))}
    </section>
  );
}

// ─── Recent engagements ───────────────────────────────────────────────────

function RecentEngagements({ state }: { state: EngagementsState }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (state.kind === "loading") {
    return (
      <section className="py-6" data-testid="dashboard-recent-loading">
        <SectionHeader title={t("dashboard.recent.title")} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="p-4 animate-pulse">
              <div className="h-4 w-2/3 rounded bg-muted mb-2" />
              <div className="h-3 w-1/2 rounded bg-muted mb-4" />
              <div className="h-7 w-16 rounded bg-muted" />
            </Card>
          ))}
        </div>
      </section>
    );
  }

  if (state.entries.length === 0) return null;

  const recent = [...state.entries]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, 3);

  return (
    <section className="py-6" data-testid="dashboard-recent">
      <SectionHeader
        title={t("dashboard.recent.title")}
        action={
          <Link
            to="/history"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            data-testid="dashboard-recent-view-all"
          >
            {t("dashboard.recent.viewAll")}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        }
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {recent.map((engagement) => (
          <RecentCard
            key={engagement.id}
            engagement={engagement}
            onOpen={() => {
              const route = engagement.lastResult
                ? `/engagements/${engagement.id}/results/balance-sheet`
                : `/engagements/${engagement.id}/contracts`;
              navigate(route);
            }}
          />
        ))}
      </div>
    </section>
  );
}

function RecentCard({
  engagement,
  onOpen,
}: {
  engagement: EngagementHistoryEntry;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const status = deriveEngagementStatus(engagement);
  const periodDisplay = formatAuditPeriod(
    engagement.auditPeriodStart,
    engagement.auditPeriodEnd,
  );
  return (
    <Card
      className="p-4 flex flex-col gap-3 transition-colors hover:border-primary/40"
      data-testid={`dashboard-recent-card-${engagement.id}`}
    >
      <div className="space-y-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">
          {engagement.clientName}
        </p>
        <p className="text-xs text-muted-foreground">{periodDisplay}</p>
      </div>
      <div className="flex items-center justify-between gap-2">
        <StatusBadge status={status} />
        <Button
          size="sm"
          variant="outline"
          onClick={onOpen}
          data-testid={`dashboard-recent-open-${engagement.id}`}
        >
          {t("dashboard.recent.open")}
        </Button>
      </div>
    </Card>
  );
}

function SectionHeader({
  title,
  action,
}: {
  title: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      {action}
    </div>
  );
}

function formatAuditPeriod(startIso: string, endIso: string): string {
  return `${safeFormat(startIso)} — ${safeFormat(endIso)}`;
}

function safeFormat(iso: string): string {
  try {
    return format(parseISO(iso), "dd.MM.yyyy");
  } catch {
    return iso;
  }
}

// ─── Trust strip ─────────────────────────────────────────────────────────

function TrustStrip() {
  const { t } = useTranslation();
  const items = [
    { icon: Lock, text: t("dashboard.trust.local") },
    { icon: Zap, text: t("dashboard.trust.backend") },
    { icon: ClipboardList, text: t("dashboard.trust.ifrs") },
  ];
  return (
    <section className="border-t py-6 mt-2" data-testid="dashboard-trust-strip">
      <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-muted-foreground">
        {items.map(({ icon: Icon, text }) => (
          <div key={text} className="flex items-center gap-2">
            <Icon className="h-4 w-4" aria-hidden="true" />
            <span>{text}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
